import type { Track, Video } from '@blackblack/shared';
import { formatCount, formatDuration } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useTrack } from '../../lib/entities';
import { useInfinite, useQuery } from '../../lib/query';
import { Link, navigate, useParams } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { player, usePlayer } from '../../state/player';
import { Icon } from '../../components/ui/Icon';
import { Button, EmptyState, ErrorState, IconButton, Skeleton } from '../../components/ui/primitives';
import { Page, PageHeader } from '../../components/layout';
import { VideoGrid } from '../../components/VideoGrid';
import { Cover } from './Cover';
import { useTrackActions } from './useTrackActions';
import styles from './music.module.css';

/**
 * Sound page — the bridge between music and video: listen to the track,
 * save it, add it to a playlist, and browse every video that uses it.
 */
export function TrackPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery<Track>(`track:${id}`, () => api.get(`/music/tracks/${id}`));
  const videos = useInfinite<Video>(`sound-videos:${id}`, (cursor) => api.page<Video>(`/music/tracks/${id}/videos`, { cursor }));

  return (
    <Page>
      <PageHeader title="Geluid" fallback="/music" />
      {q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : q.data ? <TrackHero track={q.data} /> : <HeroSkeleton />}
      <h2 className={styles.gridTitle}>
        <Icon name="grid" size={18} /> Video's met dit geluid
      </h2>
      <VideoGrid
        items={videos.items}
        loading={videos.initialLoading}
        hasMore={videos.hasMore}
        loadingMore={videos.loading}
        onLoadMore={videos.loadMore}
        empty={<EmptyState icon="grid" title="Nog geen video's" text="Maak als eerste een video met dit geluid." />}
      />
    </Page>
  );
}

function TrackHero({ track: base }: { track: Track }) {
  const track = useTrack(base);
  const { requireAuth } = useAuth();
  const actions = useTrackActions();
  const isCurrent = usePlayer((s) => s.queue[s.index]?.id === track.id);
  const playing = usePlayer((s) => s.playing) && isCurrent;

  return (
    <div className={styles.hero}>
      <Cover src={track.coverUrl} size={132} radius={16} />
      <div className={styles.heroText}>
        <h1>{track.title}</h1>
        <Link to={`/artist/${track.artist.id}`} className={styles.heroArtist}>
          {track.artist.name}
        </Link>
        <p className={styles.heroMeta}>
          {formatCount(track.videoCount)} video's · {formatCount(track.playCount)} keer afgespeeld · {formatDuration(track.durationSec)}
        </p>
        {track.album && (
          <Link to={`/album/${track.album.id}`} className={styles.heroMeta}>
            Album: {track.album.title}
          </Link>
        )}
        {!track.fullPlayback && track.streamUrl && <p className={styles.rightsNote}>Alleen een preview van {formatDuration(track.clip?.durationSec ?? 30)} is beschikbaar.</p>}
        {!track.streamUrl && <p className={styles.rightsNote}>Dit nummer is niet beschikbaar.</p>}
      </div>
      <div className={styles.heroActions}>
        <Button icon={playing ? 'pause' : 'play'} disabled={!track.streamUrl} onClick={() => (isCurrent ? player.toggle() : player.playTrack(track, `Geluid: ${track.title}`))}>
          {playing ? 'Pauze' : track.fullPlayback ? 'Afspelen' : 'Preview'}
        </Button>
        <IconButton icon="heart" label={track.liked ? 'Niet meer leuk' : 'Opslaan'} active={track.liked} filled={track.liked} onClick={() => actions.toggleLike(track)} />
        <IconButton icon="playlistAdd" label="Toevoegen aan playlist" onClick={() => actions.addToPlaylist(track)} />
        <IconButton icon="share" label="Delen" onClick={() => actions.share(track)} />
      </div>
      {track.usableInVideos && (
        <Button variant="secondary" block icon="camera" onClick={() => requireAuth('Log in om video’s te maken') && navigate(`/create?track=${track.id}`)}>
          Dit geluid gebruiken
        </Button>
      )}
    </div>
  );
}

function HeroSkeleton() {
  return (
    <div className={styles.hero}>
      <Skeleton w={132} h={132} r={16} />
      <div className={styles.heroText}>
        <Skeleton w="70%" h={22} />
        <Skeleton w="40%" h={16} />
      </div>
    </div>
  );
}
